import {NavLinkClickEvent} from '@wf-components/navigation-consumer';
import {MouseEvent, useCallback} from 'react';
import {genRoute} from '../utils/route.utils';

const isRelative = (href?: string): boolean => {
	return Boolean(href?.startsWith('/')) && !href?.startsWith('//');
};

function isAnchorElement<T>(
	e: MouseEvent<HTMLAnchorElement> | MouseEvent<T> | CustomEvent<T>
): e is MouseEvent<HTMLAnchorElement> {
	return (e as MouseEvent<HTMLAnchorElement>)?.currentTarget?.href !== undefined;
}

function isNavElement<T>(
	e: MouseEvent<T> | CustomEvent<T> | CustomEvent<NavLinkClickEvent>
): e is CustomEvent<NavLinkClickEvent> {
	return (e as CustomEvent<NavLinkClickEvent>)?.detail?.link?.hrefTemplate !== undefined;
}

export function useGlobalClickRouteHandler<T>(
	locales: string[],
	navOwner: string
): (e: MouseEvent<T> | CustomEvent<T>) => boolean {
	/**
	 * Returns true when the element clicked has a relative href that belongs to this app
	 */
	return useCallback(
		(e: MouseEvent<T> | CustomEvent<T>): boolean => {
			let skip = false;

			let hrefAttribute = '';
			let href = '';
			let localePathname = '';
			let url: URL;

			if (isNavElement(e)) {
				if (e.detail.link?.owner !== navOwner) {
					return false;
				}
				hrefAttribute = e.detail.link.relativePath || '';
				href = e.detail.link.hrefTemplate || '';
			}

			if (isAnchorElement(e)) {
				hrefAttribute = e.currentTarget.getAttribute('href') || '';
				href = e.currentTarget?.href || '';
			}

			if (href) {
				// only check relative paths
				if (isRelative(hrefAttribute)) {
					localePathname = href;

					if (
						process.env.NEXT_PUBLIC_ROUTING_OVERRIDE === true ||
						process.env.NEXT_PUBLIC_ROUTING_OVERRIDE === 'true'
					) {
						skip = true;
					} else {
						url = new URL(href);
						localePathname = genRoute(url.pathname || '/');
					}
				} else {
					//absolute paths we should ignore
					return false;
				}

				// if not this app then route to the routing base
				if (!skip) {
					e.preventDefault();

					//timeout ensures the function returns and then the navigation executes
					setTimeout(() => {
						const routingBaseUrl = new URL(
							`${localePathname}${url?.search || ''}${url?.hash || ''}`
						);
						const routeSearchParams = new URLSearchParams(routingBaseUrl.search);

						//take the current url params and hash
						const currentUrl = new URL(location.href);
						currentUrl.searchParams.forEach((value, key) => {
							routeSearchParams.set(key, value);
						});

						//apply the href params and hash on top
						routingBaseUrl.searchParams.forEach((value, key) => {
							routeSearchParams.set(key, value);
						});

						//create a final url to redirect to
						const finalSearch =
							routeSearchParams.size > 0 ? `?${routeSearchParams.toString()}` : '';
						const finalUrl = new URL(
							`${localePathname}${finalSearch}${url?.hash || ''}`
						);
						location.href = finalUrl.toString();
					}, 100);
				}
			}

			return skip;
		},
		[navOwner]
	);
}
